import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { partsApi, scooterApi, PART_CATEGORIES } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Package, MapPin, Link as LinkIcon } from "lucide-react";

export default function EditPartPage() {
  const { partId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scooters, setScooters] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    category: "autres",
    part_number: "",
    storage_location: "",
    compatible_brands: "",
    compatible_models: "",
    compatible_years: "",
    condition: "bon",
    quantity: 1,
    price: 0,
    source_scooter_id: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [partId]);

  const fetchData = async () => {
    try {
      const [partData, scootersData] = await Promise.all([
        partsApi.getById(partId),
        scooterApi.getAll()
      ]);
      
      setFormData({
        name: partData.name || "",
        category: partData.category || "autres",
        part_number: partData.part_number || "",
        storage_location: partData.storage_location || "",
        compatible_brands: partData.compatible_brands?.join(", ") || "",
        compatible_models: partData.compatible_models?.join(", ") || "",
        compatible_years: partData.compatible_years?.join(", ") || "",
        condition: partData.condition || "bon",
        quantity: partData.quantity || 1,
        price: partData.price || 0,
        source_scooter_id: partData.source_scooter_id || "",
        notes: partData.notes || ""
      });
      setScooters(scootersData);
    } catch (error) {
      toast.error("Piesa nu a fost gasita");
      navigate("/parts");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Numele piesei este obligatoriu");
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        category: formData.category,
        part_number: formData.part_number.trim(),
        storage_location: formData.storage_location.trim(),
        compatible_brands: formData.compatible_brands
          ? formData.compatible_brands.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        compatible_models: formData.compatible_models
          ? formData.compatible_models.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        compatible_years: formData.compatible_years
          ? formData.compatible_years.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n))
          : [],
        condition: formData.condition,
        quantity: parseInt(formData.quantity) || 0,
        price: parseFloat(formData.price) || 0,
        source_scooter_id: formData.source_scooter_id === "none" ? "" : formData.source_scooter_id,
        notes: formData.notes
      };

      await partsApi.update(partId, submitData);
      toast.success("Piesa a fost actualizata cu succes!");
      navigate(`/parts/${partId}`);
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Incarcare...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="edit-part-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/parts/${partId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-black text-2xl">Modifica Piesa</h1>
            <p className="text-muted-foreground text-sm">{formData.name}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Informatii de baza
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nume piesa *</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Carena fata stanga"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Categorie</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                    <SelectTrigger data-testid="select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PART_CATEGORIES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="part_number">Numar piesa / Referinta</Label>
                  <Input
                    id="part_number"
                    placeholder="Ex: ABC-123-XYZ"
                    value={formData.part_number}
                    onChange={(e) => handleChange("part_number", e.target.value)}
                    data-testid="input-part-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="condition">Stare</Label>
                  <Select value={formData.condition} onValueChange={(v) => handleChange("condition", v)}>
                    <SelectTrigger data-testid="select-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neuf">Nou</SelectItem>
                      <SelectItem value="bon">Stare buna</SelectItem>
                      <SelectItem value="use">Uzat</SelectItem>
                      <SelectItem value="reparer">De reparat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Cantitate</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.quantity || ""}
                    onChange={(e) => handleChange("quantity", e.target.value === "" ? 0 : parseInt(e.target.value) || 0)}
                    data-testid="input-quantity"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Pret (EUR)</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.price || ""}
                    onChange={(e) => handleChange("price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                    data-testid="input-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage_location" className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Locatie (Raft)
                  </Label>
                  <Input
                    id="storage_location"
                    placeholder="Ex: A1-3"
                    value={formData.storage_location}
                    onChange={(e) => handleChange("storage_location", e.target.value)}
                    data-testid="input-storage"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Compatibility */}
          <Card>
            <CardHeader>
              <CardTitle>Compatibilitate</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="compatible_brands">Marci compatibile</Label>
                <Input
                  id="compatible_brands"
                  placeholder="Ex: Peugeot, Kymco, Sym (separate prin virgula)"
                  value={formData.compatible_brands}
                  onChange={(e) => handleChange("compatible_brands", e.target.value)}
                  data-testid="input-brands"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="compatible_models">Modele compatibile</Label>
                <Input
                  id="compatible_models"
                  placeholder="Ex: Kisbee, Agility, Orbit (separate prin virgula)"
                  value={formData.compatible_models}
                  onChange={(e) => handleChange("compatible_models", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="compatible_years">Ani compatibili</Label>
                <Input
                  id="compatible_years"
                  placeholder="Ex: 2018, 2019, 2020, 2021 (separati prin virgula)"
                  value={formData.compatible_years}
                  onChange={(e) => handleChange("compatible_years", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Source Scooter */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-primary" />
                Scooter sursa (optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="source_scooter">Piesa provine de la</Label>
                <Select 
                  value={formData.source_scooter_id || "none"} 
                  onValueChange={(v) => handleChange("source_scooter_id", v)}
                >
                  <SelectTrigger data-testid="select-source-scooter">
                    <SelectValue placeholder="Selecteaza un scooter (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niciunul</SelectItem>
                    {scooters.map((scooter) => (
                      <SelectItem key={scooter.id} value={scooter.scooter_id}>
                        {scooter.brand} {scooter.model} {scooter.year} ({scooter.scooter_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Note</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Note aditionale despre piesa..."
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                rows={3}
                data-testid="input-notes"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/parts/${partId}`)}
            >
              Anuleaza
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-primary text-black font-bold"
              data-testid="btn-save-part"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Salvare..." : "Salveaza"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
